import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Message } from "primereact/message";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { appMutedTextStyle } from "../../../components/general/uiSurfaceStyles";
import { CarePlanSectionProps } from "../../../constants/constants";
import {
  BODY_CHART_CARE_PLAN_TYPE,
  BODY_CHART_IMAGE_PATH,
  BODY_CHART_MARKER_OPTIONS,
  BODY_CHART_MARKER_OPTION_BY_CODE,
  BodyChartMarker,
  BodyChartMarkerCode,
  BodyChartView,
  normalizeBodyChartMarkers,
} from "../../../constants/carePlanBodyChart";
import { useCarePlanAutoSave } from "./useCarePlanAutoSave";

const buildDefaultValues = (sectionData: any) => ({
  carePlanType: BODY_CHART_CARE_PLAN_TYPE,
  bodyChartMarkers: normalizeBodyChartMarkers(sectionData?.bodyChartMarkers),
  bodyChartNotes: String(sectionData?.bodyChartNotes || "").trim(),
});

const panelBackgroundStyle = (view: BodyChartView): React.CSSProperties => ({
  width: "100%",
  maxWidth: "290px",
  aspectRatio: "5 / 8",
  margin: "0 auto",
  borderRadius: "18px",
  border: "1px solid #dbe7f3",
  backgroundColor: "#ffffff",
  backgroundImage: `url(${BODY_CHART_IMAGE_PATH})`,
  backgroundRepeat: "no-repeat",
  backgroundSize: "200% 100%",
  backgroundPosition: view === "front" ? "left center" : "right center",
  cursor: "crosshair",
  position: "relative",
  overflow: "hidden",
  boxShadow: "inset 0 1px 0 rgba(255,255,255,0.9)",
});

type MarkerMenuState = {
  view: BodyChartView;
  x: number;
  y: number;
  left: number;
  top: number;
  markerId?: string;
} | null;

const BodyChartSection: React.FC<CarePlanSectionProps> = ({
  sectionData,
  submitButtonClick,
  backButtonClick,
  nextButtonClick,
  loadingCarePlan,
  contextNote,
}) => {
  const [enableEditing, setEnableEditing] = useState(false);
  const [draggingMarkerId, setDraggingMarkerId] = useState<string>("");
  const [markerMenu, setMarkerMenu] = useState<MarkerMenuState>(null);
  const panelRefs = useRef<Record<BodyChartView, HTMLDivElement | null>>({ front: null, back: null });
  const panelCardRefs = useRef<Record<BodyChartView, HTMLDivElement | null>>({ front: null, back: null });
  const markerMenuRef = useRef<HTMLDivElement | null>(null);
  const dragStateRef = useRef<{
    markerId: string;
    view: BodyChartView;
    moved: boolean;
    pointerId: number;
  } | null>(null);
  const suppressMarkerMenuRef = useRef(false);
  const defaultValues = useMemo(() => buildDefaultValues(sectionData), [sectionData]);
  const { watch, setValue, handleSubmit, reset, getValues } = useForm({ defaultValues });
  const bodyChartMarkers = watch("bodyChartMarkers", defaultValues.bodyChartMarkers) as BodyChartMarker[];
  const normalizedMarkers = useMemo(() => normalizeBodyChartMarkers(bodyChartMarkers), [bodyChartMarkers]);

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const onSubmit = async (data: any) => {
    submitButtonClick && submitButtonClick({
      ...data,
      carePlanType: BODY_CHART_CARE_PLAN_TYPE,
      bodyChartMarkers: normalizeBodyChartMarkers(data?.bodyChartMarkers),
    });
  };

  const onManualSave = async (data: any) => {
    submitButtonClick && submitButtonClick({
      ...data,
      carePlanType: BODY_CHART_CARE_PLAN_TYPE,
      bodyChartMarkers: normalizeBodyChartMarkers(data?.bodyChartMarkers),
    }, { showSuccessToast: true });
  };

  useCarePlanAutoSave({
    enabled: enableEditing,
    baselineData: defaultValues,
    watch,
    handleSubmit,
    onSubmit,
  });

  const persistBodyChartSection = async (nextMarkers: BodyChartMarker[], nextNotes?: string) => {
    const latestValues = getValues();
    await onSubmit({
      ...latestValues,
      carePlanType: BODY_CHART_CARE_PLAN_TYPE,
      bodyChartMarkers: normalizeBodyChartMarkers(nextMarkers),
      bodyChartNotes: typeof nextNotes === "string" ? nextNotes : String(latestValues?.bodyChartNotes || "").trim(),
    });
  };

  const addMarker = (view: BodyChartView, x: number, y: number, markerCode: BodyChartMarkerCode) => {
    if (!enableEditing) {
      return;
    }
    const option = BODY_CHART_MARKER_OPTION_BY_CODE[markerCode];
    const nextMarkers = [
      ...normalizedMarkers,
      {
        id: `body-marker-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        view,
        x,
        y,
        markerCode: option.code,
        markerLabel: option.label,
        note: "",
      },
    ];
    setValue("bodyChartMarkers", nextMarkers, { shouldDirty: true, shouldTouch: true });
    setMarkerMenu(null);
    void persistBodyChartSection(nextMarkers);
  };

  const updateMarkerPosition = (markerId: string, view: BodyChartView, x: number, y: number) => {
    const nextMarkers = normalizedMarkers.map((marker) => (
      marker.id === markerId
        ? { ...marker, view, x: Math.min(1, Math.max(0, x)), y: Math.min(1, Math.max(0, y)) }
        : marker
    ));
    setValue("bodyChartMarkers", nextMarkers, { shouldDirty: true, shouldTouch: true });
  };

  const removeMarker = (markerId: string) => {
    const nextMarkers = normalizedMarkers.filter((marker) => marker.id !== markerId);
    setValue("bodyChartMarkers", nextMarkers, { shouldDirty: true, shouldTouch: true });
    void persistBodyChartSection(nextMarkers);
  };

  const updateMarkerNote = (markerId: string, note: string) => {
    const nextMarkers = normalizedMarkers.map((marker) => (
      marker.id === markerId ? { ...marker, note } : marker
    ));
    setValue("bodyChartMarkers", nextMarkers, { shouldDirty: true, shouldTouch: true });
  };

  const clearMarkers = () => {
    setValue("bodyChartMarkers", [], { shouldDirty: true, shouldTouch: true });
    setMarkerMenu(null);
    void persistBodyChartSection([]);
  };

  const openMarkerMenu = (view: BodyChartView, clientX: number, clientY: number, markerId?: string) => {
    const target = panelRefs.current[view];
    const cardTarget = panelCardRefs.current[view];
    if (!target || !enableEditing) {
      return;
    }
    const bounds = target.getBoundingClientRect();
    const x = Math.min(1, Math.max(0, (clientX - bounds.left) / bounds.width));
    const y = Math.min(1, Math.max(0, (clientY - bounds.top) / bounds.height));
    const cardBounds = cardTarget?.getBoundingClientRect() || bounds;
    const menuWidth = 190;
    const menuHeight = 310;
    const left = Math.min(
      Math.max(12, clientX - cardBounds.left + 14),
      Math.max(12, cardBounds.width - menuWidth - 12),
    );
    const top = Math.min(
      Math.max(44, clientY - cardBounds.top + 14),
      Math.max(44, cardBounds.height - menuHeight - 12),
    );
    setMarkerMenu({
      view,
      x,
      y,
      left,
      top,
      markerId,
    });
  };

  useEffect(() => {
    if (!enableEditing) {
      dragStateRef.current = null;
      setDraggingMarkerId("");
      setMarkerMenu(null);
      return undefined;
    }

    const handlePointerMove = (event: PointerEvent) => {
      const dragState = dragStateRef.current;
      if (!dragState) {
        return;
      }
      if (event.pointerId !== dragState.pointerId) {
        return;
      }

      const target = panelRefs.current[dragState.view];
      if (!target) {
        return;
      }

      const bounds = target.getBoundingClientRect();
      const x = (event.clientX - bounds.left) / bounds.width;
      const y = (event.clientY - bounds.top) / bounds.height;
      dragState.moved = true;
      updateMarkerPosition(dragState.markerId, dragState.view, x, y);
    };

    const handlePointerUp = (event: PointerEvent) => {
      const dragState = dragStateRef.current;
      if (dragState && event.pointerId !== dragState.pointerId) {
        return;
      }
      const shouldPersist = !!dragState?.moved;
      suppressMarkerMenuRef.current = shouldPersist;
      dragStateRef.current = null;
      setDraggingMarkerId("");
      if (shouldPersist) {
        const latestValues = getValues();
        void persistBodyChartSection(normalizeBodyChartMarkers(latestValues?.bodyChartMarkers));
      }
    };

    const handlePointerCancel = (event: PointerEvent) => {
      const dragState = dragStateRef.current;
      if (dragState && event.pointerId !== dragState.pointerId) {
        return;
      }
      dragStateRef.current = null;
      setDraggingMarkerId("");
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
    window.addEventListener("pointercancel", handlePointerCancel);

    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
      window.removeEventListener("pointercancel", handlePointerCancel);
    };
  }, [enableEditing, bodyChartMarkers, setValue]);

  useEffect(() => {
    if (!enableEditing || !markerMenu) {
      return undefined;
    }

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (markerMenuRef.current?.contains(target)) {
        return;
      }
      const clickedPanel = Object.values(panelRefs.current).some((panel) => panel?.contains(target || null));
      if (!clickedPanel) {
        setMarkerMenu(null);
      }
    };

    window.addEventListener("pointerdown", handlePointerDown);
    return () => {
      window.removeEventListener("pointerdown", handlePointerDown);
    };
  }, [enableEditing, markerMenu]);

  const actions = () => (
    <div className="flex justify-content-center gap-2 flex-wrap">
      <Button label="Edit" icon="pi pi-pencil" severity="warning" outlined rounded onClick={() => setEnableEditing(true)} visible={!enableEditing} />
      <Button label="Disable Edit" icon="pi pi-lock" visible={enableEditing} severity="secondary" outlined rounded onClick={() => { setEnableEditing(false); reset(defaultValues); }} />
      <Button label="Save" loading={loadingCarePlan} disabled={!enableEditing} severity="warning" outlined rounded onClick={handleSubmit(onManualSave)} visible={enableEditing} />
      <Button label="Back" icon="pi pi-arrow-left" severity="secondary" outlined rounded onClick={backButtonClick} visible={!!backButtonClick} />
      <Button label="Next" icon="pi pi-arrow-right" severity="info" outlined rounded iconPos="right" onClick={nextButtonClick} visible={!!nextButtonClick} />
    </div>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1em" }} className="m-2 p-component resident-careplan-section">
      <Card className="resident-careplan-card" footer={actions()} header={actions()}>
        <div className="flex flex-column gap-4">
          <div
            style={{
              border: "1px solid #dbe4f0",
              borderRadius: "16px",
              background: "#f8fafc",
              padding: "1rem",
            }}
          >
            <div className="flex justify-content-between align-items-start gap-3 flex-wrap" style={{ marginBottom: "0.9rem" }}>
              <div>
                <div style={{ fontWeight: 700, color: "#0f172a" }}>Body Chart</div>
                <div style={{ ...appMutedTextStyle, marginTop: "0.35rem" }}>
                  Click the front or back body image to choose a marker right on the chart. Drag a placed marker to reposition it, and use Remove in the details list when you want to delete one.
                </div>
                {contextNote ? (
                  <div style={{ marginTop: "0.75rem" }}>
                    <Message
                      severity="info"
                      text={`Note: ${contextNote}`}
                    />
                  </div>
                ) : null}
              </div>
              <div className="flex gap-2 align-items-center flex-wrap">
                <Button
                  type="button"
                  label="Clear Markers"
                  icon="pi pi-times"
                  outlined
                  severity="secondary"
                  onClick={clearMarkers}
                  disabled={!enableEditing || normalizedMarkers.length === 0}
                />
              </div>
            </div>

            <div className="grid">
              {(["front", "back"] as BodyChartView[]).map((view) => (
                <div key={view} className="col-12 md:col-6">
                  <div
                    ref={(element) => {
                      panelCardRefs.current[view] = element;
                    }}
                    style={{
                      border: "1px solid #cbd5e1",
                      borderRadius: "16px",
                      background: "#ffffff",
                      padding: "0.85rem",
                      position: "relative",
                    }}
                  >
                    <div style={{ fontWeight: 700, color: "#0f172a", marginBottom: "0.65rem", textTransform: "capitalize" }}>{view} body view</div>
                    <div
                      role="button"
                      tabIndex={0}
                      ref={(element) => {
                        panelRefs.current[view] = element;
                      }}
                      onClick={(event) => {
                        if (dragStateRef.current?.moved) {
                          dragStateRef.current = null;
                          return;
                        }
                        openMarkerMenu(view, event.clientX, event.clientY);
                      }}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                        }
                      }}
                      style={panelBackgroundStyle(view)}
                    >
                      {normalizedMarkers.map((marker, index) => ({ marker, index }))
                        .filter(({ marker }) => marker.view === view)
                        .map(({ marker, index }) => {
                        const option = BODY_CHART_MARKER_OPTION_BY_CODE[marker.markerCode];
                        return (
                          <button
                            key={marker.id}
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              if (!enableEditing) {
                                return;
                              }
                              if (suppressMarkerMenuRef.current) {
                                suppressMarkerMenuRef.current = false;
                                return;
                              }
                              openMarkerMenu(view, event.clientX, event.clientY, marker.id);
                            }}
                            onPointerDown={(event) => {
                              event.preventDefault();
                              event.stopPropagation();
                              if (!enableEditing) {
                                return;
                              }
                              event.currentTarget.setPointerCapture?.(event.pointerId);
                              dragStateRef.current = {
                                markerId: marker.id,
                                view,
                                moved: false,
                                pointerId: event.pointerId,
                              };
                              setDraggingMarkerId(marker.id);
                            }}
                            onPointerUp={(event) => {
                              event.currentTarget.releasePointerCapture?.(event.pointerId);
                            }}
                            onPointerCancel={(event) => {
                              event.currentTarget.releasePointerCapture?.(event.pointerId);
                            }}
                            onDragStart={(event) => {
                              event.preventDefault();
                            }}
                            title={enableEditing ? `Drag to reposition #${index + 1} ${marker.markerLabel} (${marker.view})` : `#${index + 1} ${marker.markerLabel} (${marker.view})`}
                            style={{
                              position: "absolute",
                              left: `${marker.x * 100}%`,
                              top: `${marker.y * 100}%`,
                              transform: "translate(-50%, -50%)",
                              width: String(index + 1).length > 1 ? "34px" : "30px",
                              height: "30px",
                              borderRadius: "999px",
                              border: "2px solid rgba(15, 23, 42, 0.18)",
                              background: option.color,
                              color: option.textColor,
                              fontSize: "0.78rem",
                              fontWeight: 800,
                              cursor: enableEditing ? (draggingMarkerId === marker.id ? "grabbing" : "grab") : "default",
                              boxShadow: "0 8px 18px rgba(15,23,42,0.18)",
                              touchAction: "none",
                              userSelect: "none",
                              WebkitUserSelect: "none",
                              zIndex: draggingMarkerId === marker.id ? 2 : 1,
                            }}
                          >
                            {index + 1}
                          </button>
                        );
                      })}
                    </div>
                    {markerMenu?.view === view ? (
                      <div
                        ref={markerMenuRef}
                        style={{
                          position: "absolute",
                          left: markerMenu.left,
                          top: markerMenu.top,
                          width: "190px",
                          borderRadius: "14px",
                          border: "1px solid #cbd5e1",
                          background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
                          boxShadow: "0 18px 38px rgba(15,23,42,0.16)",
                          padding: "0.55rem",
                          zIndex: 6,
                        }}
                        onClick={(event) => {
                          event.stopPropagation();
                        }}
                      >
                        <div className="flex justify-content-between align-items-center gap-2" style={{ marginBottom: "0.45rem" }}>
                          <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "0.85rem" }}>
                            Choose Marker
                          </div>
                          <Button
                            type="button"
                            text
                            icon="pi pi-times"
                            onClick={() => setMarkerMenu(null)}
                            style={{ color: "#64748b", padding: "0.1rem", width: "1.8rem", height: "1.8rem" }}
                          />
                        </div>
                        <div className="flex flex-column gap-1">
                          {markerMenu.markerId ? (
                            <button
                              type="button"
                              onClick={() => removeMarker(markerMenu.markerId!)}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "0.55rem",
                                width: "100%",
                                borderRadius: "10px",
                                border: "1px solid #fecaca",
                                background: "#fff5f5",
                                color: "#b91c1c",
                                padding: "0.5rem 0.6rem",
                                fontSize: "0.82rem",
                                fontWeight: 700,
                                cursor: "pointer",
                                textAlign: "left",
                              }}
                            >
                              <i className="pi pi-trash" />
                              <span>Delete Marker</span>
                            </button>
                          ) : null}
                          {BODY_CHART_MARKER_OPTIONS.map((option) => (
                            <button
                              key={option.code}
                              type="button"
                              onClick={() => addMarker(markerMenu.view, markerMenu.x, markerMenu.y, option.code)}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "0.55rem",
                                width: "100%",
                                borderRadius: "10px",
                                border: "1px solid #dbe4f0",
                                background: "#ffffff",
                                color: "#0f172a",
                                padding: "0.5rem 0.6rem",
                                fontSize: "0.82rem",
                                fontWeight: 600,
                                cursor: "pointer",
                                textAlign: "left",
                              }}
                            >
                              <span
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  width: "24px",
                                  height: "24px",
                                  borderRadius: "999px",
                                  background: option.color,
                                  color: option.textColor,
                                  fontWeight: 800,
                                  fontSize: "0.72rem",
                                  flexShrink: 0,
                                }}
                              >
                                {option.code}
                              </span>
                              <span>{option.label}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex gap-2 flex-wrap" style={{ marginTop: "0.95rem" }}>
              {BODY_CHART_MARKER_OPTIONS.map((option) => {
                const active = normalizedMarkers.some((marker) => marker.markerCode === option.code);
                return (
                  <div
                    key={option.code}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "0.45rem",
                      borderRadius: "999px",
                      padding: "0.45rem 0.75rem",
                      border: active ? `1px solid ${option.color}` : "1px solid #cbd5e1",
                      background: active ? `${option.color}22` : "#ffffff",
                      color: "#0f172a",
                      fontSize: "0.86rem",
                      fontWeight: active ? 700 : 500,
                    }}
                  >
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minWidth: "28px",
                        height: "28px",
                        borderRadius: "999px",
                        background: option.color,
                        color: option.textColor,
                        fontWeight: 800,
                        fontSize: "0.76rem",
                      }}
                    >
                      {option.code}
                    </span>
                    {option.label}
                  </div>
                );
              })}
            </div>
          </div>

          <div>
            <div style={{ fontWeight: 700, color: "#0f172a", marginBottom: "0.65rem" }}>Marker Details</div>
            {normalizedMarkers.length === 0 ? (
              <div style={appMutedTextStyle}>No markers added yet.</div>
            ) : (
              <div className="flex flex-column gap-2">
                {normalizedMarkers.map((marker, index) => (
                  <div
                    key={marker.id}
                    style={{
                      border: "1px solid #e2e8f0",
                      borderRadius: "14px",
                      padding: "0.85rem",
                      background: "#ffffff",
                    }}
                  >
                    <div className="flex justify-content-between gap-3 align-items-center flex-wrap">
                      <div style={{ fontWeight: 700, color: "#0f172a" }}>
                        {index + 1}. {marker.markerLabel} ({marker.view})
                      </div>
                      <Button
                        type="button"
                        icon="pi pi-trash"
                        label="Remove"
                        severity="danger"
                        text
                        onClick={() => removeMarker(marker.id)}
                        disabled={!enableEditing}
                      />
                    </div>
                    <div className="mt-2">
                      <InputText
                        value={marker.note || ""}
                        onChange={(event) => updateMarkerNote(marker.id, event.target.value)}
                        className="w-full"
                        placeholder="Add a short note for the legend and document"
                        disabled={!enableEditing}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <div style={{ fontWeight: 700, color: "#0f172a", marginBottom: "0.65rem" }}>Overall Notes</div>
            <InputTextarea
              id="bodyChartNotes"
              value={watch("bodyChartNotes", defaultValues.bodyChartNotes)}
              onChange={(event) => setValue("bodyChartNotes", event.target.value, { shouldDirty: true, shouldTouch: true })}
              rows={4}
              className="w-full"
              autoResize
              disabled={!enableEditing}
              placeholder="Add any overall body chart notes you want to carry into the generated document."
            />
          </div>
        </div>
      </Card>
    </div>
  );
};

export default BodyChartSection;
